import { useState } from "react";
import { useParams, useNavigate } from "react-router-dom";
import Navbar from "@/components/feature/Navbar";
import AuthModal from "@/components/feature/AuthModal";
import { useGame, Resident, calcHouseIncome } from "@/contexts/GameContext";
import { useAuth } from "@/contexts/AuthContext";
import { useTheme } from "@/contexts/ThemeContext";
import { getLandValueByLatLng } from "@/mocks/landValues";

const MOCK_APPLICANTS: Omit<Resident, "id" | "movedInAt">[] = [
  { name: "山田 太郎", age: 28, occupation: "エンジニア" },
  { name: "佐藤 美咲", age: 34, occupation: "看護師" },
  { name: "鈴木 健一", age: 42, occupation: "教師" },
  { name: "田中 由美", age: 25, occupation: "デザイナー" },
  { name: "中村 浩二", age: 55, occupation: "会社員" },
  { name: "小林 さくら", age: 31, occupation: "医師" },
];

export default function HousePage() {
  const { houseId } = useParams<{ houseId: string }>();
  const { houses } = useGame();
  const { user } = useAuth();
  const { isDark } = useTheme();
  const navigate = useNavigate();
  const [tab, setTab] = useState<"residents" | "applicants">("residents");
  const [msg, setMsg] = useState<{ text: string; type: "success" | "error" } | null>(null);
  const [residents, setResidents] = useState<Resident[]>(() => {
    const h = houseId ? houses[houseId] : null;
    return h ? h.residents : [];
  });

  const house = houseId ? houses[houseId] : null;
  const isOwner = !!(user && house && house.ownerId === user.id);

  // 土地の座標を blocks から取得して地価係数を計算
  const { blocks } = useGame();
  const landBlock = house ? Object.values(blocks).find((b) => b.blockId === house.blockId) : null;
  const blockLat = landBlock?.lat ?? 35.6812;
  const blockLng = landBlock?.lng ?? 139.7671;
  const lv = getLandValueByLatLng(blockLat, blockLng);
  const perResidentIncome = lv.houseIncome; // coins/h per resident

  const pageBg = isDark ? "bg-[#0a0a0f]" : "bg-[#f8f8f8]";
  const textPrimary = isDark ? "text-white" : "text-gray-900";
  const textSecondary = isDark ? "text-gray-400" : "text-gray-500";
  const cardBg = isDark ? "bg-[#0f1015] border-white/8" : "bg-white border-gray-200";
  const rowHover = isDark ? "hover:bg-white/3" : "hover:bg-gray-50";
  const divider = isDark ? "divide-white/5" : "divide-gray-100";

  const maxResidents = house?.maxResidents ?? 4;

  const handleMoveIn = (applicant: Omit<Resident, "id" | "movedInAt">) => {
    if (residents.length >= maxResidents) {
      setMsg({ text: "満室です。空きがありません。", type: "error" });
      return;
    }
    const alreadyIn = residents.some((r) => r.name === applicant.name);
    if (alreadyIn) {
      setMsg({ text: "すでに入居済みです", type: "error" });
      return;
    }
    const newResident: Resident = {
      ...applicant,
      id: `res-${Date.now()}`,
      movedInAt: new Date().toISOString(),
    };
    setResidents((prev) => [...prev, newResident]);
    setMsg({ text: `${applicant.name}が入居しました！`, type: "success" });
    setTimeout(() => setMsg(null), 3000);
  };

  const handleMoveOut = (residentId: string) => {
    const r = residents.find((r) => r.id === residentId);
    setResidents((prev) => prev.filter((r) => r.id !== residentId));
    setMsg({ text: `${r?.name ?? "居住者"}が退去しました`, type: "success" });
    setTimeout(() => setMsg(null), 3000);
  };

  if (!house) {
    return (
      <div className={`min-h-screen ${pageBg}`}>
        <Navbar />
        <div className="pt-20 flex flex-col items-center justify-center min-h-[60vh] text-center px-4">
          <div className="w-16 h-16 flex items-center justify-center bg-red-400/10 rounded-2xl mb-4">
            <i className="ri-home-4-line text-3xl text-red-400"></i>
          </div>
          <h2 className={`text-xl font-bold mb-2 ${textPrimary}`}>住宅が見つかりません</h2>
          <button onClick={() => navigate("/mypage")} className="mt-4 bg-gradient-to-r from-amber-400 to-orange-500 text-black font-bold rounded-full px-6 py-2.5 cursor-pointer whitespace-nowrap">
            マイページへ戻る
          </button>
        </div>
        <AuthModal />
      </div>
    );
  }

  const occupancyRate = Math.round((residents.length / maxResidents) * 100);
  // 居住者がいないと収益ゼロ。地価係数 × 居住者数 × perResidentIncome coins/h
  const hourlyIncome = calcHouseIncome(residents.length, blockLat, blockLng);
  const monthlyIncome = hourlyIncome * 24 * 30;

  return (
    <div className={`min-h-screen ${pageBg} transition-colors duration-300`}>
      <Navbar />
      <div className="pt-20 pb-12 px-4 md:px-6 max-w-4xl mx-auto">
        {/* Breadcrumb */}
        <div className="flex items-center gap-2 mb-6">
          <button onClick={() => navigate("/mypage")} className={`text-sm cursor-pointer hover:text-amber-400 transition-colors ${textSecondary}`}>マイページ</button>
          <i className="ri-arrow-right-s-line text-gray-500 text-sm"></i>
          <span className={`text-sm ${textPrimary}`}>住宅管理</span>
        </div>

        {/* House Header */}
        <div className={`rounded-2xl border p-6 mb-6 ${cardBg}`}>
          <div className="flex flex-col sm:flex-row items-start gap-5">
            <div className={`w-16 h-16 flex items-center justify-center rounded-2xl flex-shrink-0 ${isDark ? "bg-emerald-400/10" : "bg-emerald-50"}`}>
              <i className="ri-home-4-line text-3xl text-emerald-400"></i>
            </div>
            <div className="flex-1">
              <div className="flex flex-wrap items-center gap-3 mb-2">
                <h1 className={`text-xl font-black ${textPrimary}`}>住宅</h1>
                {isOwner && <span className="text-xs px-2.5 py-1 rounded-full bg-amber-400/15 text-amber-400 font-semibold whitespace-nowrap">オーナー</span>}
                <span className={`text-xs px-2.5 py-1 rounded-full font-semibold whitespace-nowrap ${occupancyRate >= 100 ? "bg-red-400/15 text-red-400" : occupancyRate >= 50 ? "bg-amber-400/15 text-amber-400" : "bg-emerald-400/15 text-emerald-400"}`}>
                  {occupancyRate >= 100 ? "満室" : occupancyRate >= 50 ? "空きわずか" : "空室あり"}
                </span>
              </div>
              <p className={`text-sm ${textSecondary} mb-4`}>
                建設日: {new Date(house.builtAt).toLocaleDateString("ja-JP")} •
                状態: {house.condition}%
              </p>
              {/* Occupancy Bar */}
              <div className="space-y-1.5">
                <div className="flex justify-between text-xs">
                  <span className={textSecondary}>入居率</span>
                  <span className={`font-bold ${textPrimary}`}>{residents.length} / {maxResidents}名 ({occupancyRate}%)</span>
                </div>
                <div className={`h-2.5 rounded-full ${isDark ? "bg-white/10" : "bg-gray-100"}`}>
                  <div className={`h-2.5 rounded-full transition-all ${occupancyRate >= 100 ? "bg-red-400" : "bg-gradient-to-r from-emerald-400 to-emerald-500"}`}
                    style={{ width: `${occupancyRate}%` }}></div>
                </div>
              </div>
            </div>
            {/* Stats */}
            <div className="grid grid-cols-2 gap-3 flex-shrink-0">
              <div className={`rounded-xl p-3 text-center ${isDark ? "bg-white/5" : "bg-gray-50"}`}>
                <p className={`text-xs ${textSecondary} mb-1 whitespace-nowrap`}>居住者数</p>
                <p className={`font-black text-xl ${textPrimary}`}>{residents.length}</p>
                <p className={`text-xs ${textSecondary}`}>/ {maxResidents}名</p>
              </div>
              <div className={`rounded-xl p-3 text-center ${isDark ? "bg-white/5" : "bg-gray-50"}`}>
                <p className={`text-xs ${textSecondary} mb-1 whitespace-nowrap`}>時間収入</p>
                <p className={`font-black text-xl ${hourlyIncome > 0 ? "text-emerald-400" : "text-gray-500"}`}>{hourlyIncome}</p>
                <p className={`text-xs ${textSecondary}`}>coins/h</p>
              </div>
            </div>
          </div>
        </div>

        {/* Message */}
        {msg && (
          <div className={`mb-4 rounded-xl px-4 py-3 text-sm flex items-center gap-2 ${msg.type === "success" ? "bg-emerald-400/10 text-emerald-400 border border-emerald-400/20" : "bg-red-400/10 text-red-400 border border-red-400/20"}`}>
            <i className={msg.type === "success" ? "ri-checkbox-circle-line" : "ri-error-warning-line"}></i>
            {msg.text}
          </div>
        )}

        {/* Tabs */}
        <div className={`rounded-2xl border overflow-hidden mb-6 ${cardBg}`}>
          <div className="flex border-b border-white/5">
            {[
              { id: "residents" as const, label: "入居者一覧", icon: "ri-team-line" },
              { id: "applicants" as const, label: "入居希望者", icon: "ri-user-add-line" },
            ].map((t) => (
              <button key={t.id} onClick={() => setTab(t.id)}
                className={`flex items-center gap-2 px-5 py-4 text-sm font-semibold transition-all cursor-pointer whitespace-nowrap border-b-2 ${tab === t.id ? "border-amber-400 text-amber-400" : isDark ? "border-transparent text-gray-400 hover:text-white" : "border-transparent text-gray-500 hover:text-gray-900"}`}>
                <i className={`${t.icon} text-base`}></i>
                {t.label}
                {t.id === "residents" && <span className={`text-xs px-1.5 py-0.5 rounded-full ${isDark ? "bg-white/10" : "bg-gray-100"}`}>{residents.length}</span>}
              </button>
            ))}
          </div>

          {/* Residents Tab */}
          {tab === "residents" && (
            residents.length === 0 ? (
              <div className="px-6 py-12 text-center">
                <div className="w-12 h-12 flex items-center justify-center mx-auto mb-3 rounded-full bg-emerald-400/10">
                  <i className="ri-home-4-line text-2xl text-emerald-400"></i>
                </div>
                <p className={`text-sm ${textSecondary}`}>入居者がいません</p>
                {isOwner && (
                  <button onClick={() => setTab("applicants")} className="mt-3 text-xs text-amber-400 hover:underline cursor-pointer">
                    入居希望者を見る →
                  </button>
                )}
              </div>
            ) : (
              <div className={`divide-y ${divider}`}>
                {residents.map((r) => (
                  <div key={r.id} className={`flex items-center gap-4 px-6 py-4 transition-colors ${rowHover}`}>
                    <div className={`w-10 h-10 flex items-center justify-center rounded-full flex-shrink-0 font-bold text-sm ${isDark ? "bg-white/10 text-white" : "bg-gray-100 text-gray-700"}`}>
                      {r.name.charAt(0)}
                    </div>
                    <div className="flex-1 min-w-0">
                      <p className={`text-sm font-semibold ${textPrimary}`}>{r.name}</p>
                      <div className="flex items-center gap-3 mt-0.5 flex-wrap">
                        <span className={`text-xs ${textSecondary}`}>{r.age}歳</span>
                        <span className={`text-xs px-2 py-0.5 rounded-full whitespace-nowrap ${isDark ? "bg-white/5 text-gray-400" : "bg-gray-100 text-gray-500"}`}>{r.occupation}</span>
                        {r.employerId && (
                          <span className="text-xs px-2 py-0.5 rounded-full bg-cyan-400/10 text-cyan-400 whitespace-nowrap">
                            <i className="ri-building-4-line mr-1 text-xs"></i>勤務先あり
                          </span>
                        )}
                      </div>
                    </div>
                    <div className="text-right flex-shrink-0 space-y-1">
                      <p className={`text-xs ${textSecondary} whitespace-nowrap`}>
                        入居: {new Date(r.movedInAt).toLocaleDateString("ja-JP", { month: "short", day: "numeric" })}
                      </p>
                      {isOwner && (
                        <button onClick={() => handleMoveOut(r.id)}
                          className={`text-xs px-3 py-1 rounded-lg transition-colors cursor-pointer whitespace-nowrap ${isDark ? "bg-white/5 text-gray-400 hover:bg-red-500/20 hover:text-red-400" : "bg-gray-100 text-gray-500 hover:bg-red-50 hover:text-red-500"}`}>
                          <i className="ri-logout-box-line mr-1"></i>退去
                        </button>
                      )}
                    </div>
                  </div>
                ))}
              </div>
            )
          )}

          {/* Applicants Tab */}
          {tab === "applicants" && (
            <div>
              <div className={`px-6 py-3 border-b ${isDark ? "border-white/5" : "border-gray-100"}`}>
                <p className={`text-xs ${textSecondary}`}>
                  空き部屋: <span className={`font-bold ${maxResidents - residents.length > 0 ? "text-emerald-400" : "text-red-400"}`}>{maxResidents - residents.length}室</span>
                </p>
              </div>
              <div className={`divide-y ${divider}`}>
                {MOCK_APPLICANTS.map((applicant, i) => {
                  const alreadyIn = residents.some((r) => r.name === applicant.name);
                  return (
                    <div key={i} className={`flex items-center gap-4 px-6 py-4 transition-colors ${rowHover}`}>
                      <div className={`w-10 h-10 flex items-center justify-center rounded-full flex-shrink-0 font-bold text-sm ${isDark ? "bg-white/10 text-white" : "bg-gray-100 text-gray-700"}`}>
                        {applicant.name.charAt(0)}
                      </div>
                      <div className="flex-1 min-w-0">
                        <p className={`text-sm font-semibold ${textPrimary}`}>{applicant.name}</p>
                        <div className="flex items-center gap-2 mt-0.5">
                          <span className={`text-xs ${textSecondary}`}>{applicant.age}歳</span>
                          <span className={`text-xs px-2 py-0.5 rounded-full whitespace-nowrap ${isDark ? "bg-white/5 text-gray-400" : "bg-gray-100 text-gray-500"}`}>{applicant.occupation}</span>
                        </div>
                      </div>
                      {isOwner && (
                        <button onClick={() => handleMoveIn(applicant)} disabled={alreadyIn || residents.length >= maxResidents}
                          className={`flex-shrink-0 text-xs px-4 py-2 rounded-lg font-semibold transition-all cursor-pointer whitespace-nowrap ${
                            alreadyIn ? isDark ? "bg-white/5 text-gray-600 cursor-not-allowed" : "bg-gray-100 text-gray-400 cursor-not-allowed"
                            : residents.length >= maxResidents ? "bg-red-400/10 text-red-400 cursor-not-allowed"
                            : "bg-emerald-400/15 text-emerald-400 hover:bg-emerald-400/25 border border-emerald-400/30"
                          }`}>
                          {alreadyIn ? "入居済" : residents.length >= maxResidents ? "満室" : "入居させる"}
                        </button>
                      )}
                    </div>
                  );
                })}
              </div>
            </div>
          )}
        </div>

        {/* Condition Info */}
        <div className={`rounded-2xl border p-5 ${cardBg}`}>
          <h3 className={`text-sm font-bold mb-3 ${textPrimary}`}>住宅の状態</h3>
          <div className="space-y-3">
            <div>
              <div className="flex justify-between text-xs mb-1.5">
                <span className={textSecondary}>建物の状態</span>
                <span className={`font-bold ${house.condition >= 80 ? "text-emerald-400" : house.condition >= 50 ? "text-amber-400" : "text-red-400"}`}>{house.condition}%</span>
              </div>
              <div className={`h-2 rounded-full ${isDark ? "bg-white/10" : "bg-gray-100"}`}>
                <div className={`h-2 rounded-full transition-all ${house.condition >= 80 ? "bg-emerald-400" : house.condition >= 50 ? "bg-amber-400" : "bg-red-400"}`}
                  style={{ width: `${house.condition}%` }}></div>
              </div>
            </div>
            {/* 地域情報 */}
            <div className={`rounded-lg p-3 mb-3 flex items-center gap-2 ${isDark ? "bg-amber-400/5 border border-amber-400/15" : "bg-amber-50 border border-amber-200"}`}>
              <span className="text-lg">{lv.flag}</span>
              <div>
                <p className="text-xs text-amber-400 font-semibold">{lv.country}</p>
                <p className={`text-xs ${textSecondary}`}>地価係数 ×{lv.landMultiplier} — 居住者1人あたり {perResidentIncome} coins/h</p>
              </div>
            </div>
            <div className="grid grid-cols-3 gap-3 text-xs">
              <div className={`rounded-lg p-3 ${isDark ? "bg-white/3" : "bg-gray-50"}`}>
                <p className={`${textSecondary} mb-1`}>最大収容</p>
                <p className={`font-bold ${textPrimary}`}>{maxResidents}名</p>
              </div>
              <div className={`rounded-lg p-3 ${isDark ? "bg-white/3" : "bg-gray-50"}`}>
                <p className={`${textSecondary} mb-1`}>1人あたり収入</p>
                <p className="font-bold text-emerald-400">{perResidentIncome} coins/h</p>
              </div>
              <div className={`rounded-lg p-3 ${isDark ? "bg-white/3" : "bg-gray-50"}`}>
                <p className={`${textSecondary} mb-1`}>満室時収入</p>
                <p className={`font-bold ${maxResidents * perResidentIncome > 0 ? "text-emerald-400" : "text-gray-500"}`}>{maxResidents * perResidentIncome} coins/h</p>
              </div>
            </div>
            {residents.length === 0 && (
              <div className={`mt-3 rounded-lg p-3 text-xs ${isDark ? "bg-red-400/10 border border-red-400/20 text-red-400" : "bg-red-50 border border-red-200 text-red-500"}`}>
                <i className="ri-error-warning-line mr-1"></i>
                居住者がいないため収益はゼロです。入居希望者タブから入居させてください。
              </div>
            )}
          </div>
        </div>
      </div>
      <AuthModal />
    </div>
  );
}
